export const EASTERN_TZ = 'America/New_York';

function getPart(parts, type) {
  return parts.find((part) => part.type === type)?.value;
}

export function parseUtcCreateDate(dateStr) {
  if (dateStr == null || dateStr === '') return null;
  const trimmed = String(dateStr).trim();
  if (!trimmed) return null;

  if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(trimmed)) {
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(`${trimmed}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getFractionalSeconds(dateStr) {
  const match = String(dateStr).match(/\.(\d+)/);
  if (!match) return '000000';
  return match[1].padEnd(6, '0').slice(0, 6);
}

function formatEasternLocalDateTime(instant) {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: EASTERN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(instant).replace(' ', 'T');
}

function easternOffsetForInstant(instant) {
  const local = formatEasternLocalDateTime(instant);

  for (const offset of ['-04:00', '-05:00']) {
    const candidate = new Date(`${local}${offset}`);
    if (Math.abs(candidate.getTime() - instant.getTime()) < 1000) {
      return offset;
    }
  }

  return '-05:00';
}

export function convertUtcCreateDateToEastern(dateStr) {
  const instant = parseUtcCreateDate(dateStr);
  if (!instant) return dateStr;

  const easternLocal = formatEasternLocalDateTime(instant);
  const offset = easternOffsetForInstant(instant);
  const fractionalSeconds = getFractionalSeconds(dateStr);

  return `${easternLocal}.${fractionalSeconds}${offset}`;
}

export function normalizeEventsFeed(events) {
  if (!Array.isArray(events)) return [];

  return events.map((event) => {
    if (!event?.create_date) return event;
    return {
      ...event,
      create_date: convertUtcCreateDateToEastern(event.create_date),
    };
  });
}

export function getEasternYmd(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TZ,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);

  return {
    year: Number(getPart(parts, 'year')),
    month: Number(getPart(parts, 'month')),
    day: Number(getPart(parts, 'day')),
  };
}

function ymdToComparable({ year, month, day }) {
  return year * 10000 + month * 100 + day;
}

function subtractDaysFromYmd(ymd, days) {
  const anchor = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day));
  anchor.setUTCDate(anchor.getUTCDate() - days);
  return {
    year: anchor.getUTCFullYear(),
    month: anchor.getUTCMonth() + 1,
    day: anchor.getUTCDate(),
  };
}

export function isInEasternMonth(dateStr, referenceDate = new Date()) {
  if (!dateStr) return false;
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return false;

  const eventYmd = getEasternYmd(parsed);
  const referenceYmd = getEasternYmd(referenceDate);
  return eventYmd.year === referenceYmd.year && eventYmd.month === referenceYmd.month;
}

export function isWithinEasternDateRange(dateStr, daysPreset) {
  if (daysPreset === '1000') return true;
  if (!dateStr) return false;

  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return false;

  const eventYmd = getEasternYmd(parsed);
  const todayYmd = getEasternYmd(new Date());
  const cutoffYmd = subtractDaysFromYmd(todayYmd, Number(daysPreset));

  return ymdToComparable(eventYmd) >= ymdToComparable(cutoffYmd);
}

export function formatEasternDate(dateStr, options = {}) {
  if (!dateStr) return '—';
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return '—';

  return parsed.toLocaleString('en-US', {
    timeZone: EASTERN_TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}
